import { create } from "@bufbuild/protobuf";
import { getNetInfo } from "../api/client";
import type { JoinResponse, NetInfo } from "../proto/gunrun/v1/api_pb";
import { ClientMessageSchema, HelloSchema } from "../proto/gunrun/v1/game_pb";
import { decodeUnreliable, encodeInputPacket, encodePing, initWasm } from "../wasm";
import { PROTOCOL_VERSION } from "./constants";
import { GameConnection } from "./connection";
import type { ConnectionEnv, UnreliableCodec } from "./connection";
import { raceTransports } from "./race";
import type { TransportMode } from "./race";
import { WebSocketTransport } from "./websocket";
import type { WebSocketFactory } from "./websocket";
import { WebTransportTransport, webTransportSupported } from "./webtransport";
import type { WebTransportFactory } from "./webtransport";

/** The unreliable-channel codec backed by gr-wasm (the module must be initialized first). */
const wasmCodec: UnreliableCodec = { encodeInputPacket, encodePing, decode: decodeUnreliable };

export interface ConnectOptions {
  /** `settings.network.transport`. */
  transport: TransportMode;
}

/** Seams for tests; the game passes none. */
export interface ConnectDeps {
  getNetInfo?: () => Promise<NetInfo>;
  codec?: UnreliableCodec;
  env?: ConnectionEnv;
  createWebSocket?: WebSocketFactory;
  createWebTransport?: WebTransportFactory;
  location?: { protocol: string; host: string };
  fallbackMs?: number;
}

function websocketUrl(path: string, ticket: string, loc: { protocol: string; host: string }): string {
  const scheme = loc.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${loc.host}${path}?ticket=${ticket}`;
}

/**
 * Connects to the room of `join` (a `JoinResponse` from quickplay / create / join / ranked):
 * fetches the transport info (`GET /api/net`), opens WebTransport and WebSocket per
 * `docs/NETCODE.md` "Transports" (first handshake wins), sends `ClientMessage.hello` and starts
 * the ping loop. The first reliable message that arrives is `welcome`; it and any early
 * snapshots are queued until the handlers are assigned on the returned connection.
 */
export async function connectGame(
  join: JoinResponse,
  opts: ConnectOptions,
  deps: ConnectDeps = {},
): Promise<GameConnection> {
  const netInfo = await (deps.getNetInfo ?? getNetInfo)();
  if (netInfo.protocolVersion !== 0 && netInfo.protocolVersion !== PROTOCOL_VERSION) {
    throw new Error("This game client is outdated. Reload the page to update.");
  }
  let codec = deps.codec;
  if (!codec) {
    await initWasm();
    codec = wasmCodec;
  }

  const ticket = encodeURIComponent(join.ticket);
  const offered = netInfo.webtransport;
  const canUseWebTransport = deps.createWebTransport !== undefined || webTransportSupported();
  const startWebTransport =
    offered && canUseWebTransport
      ? () => {
          const sep = offered.url.includes("?") ? "&" : "?";
          const hashes = offered.certHashes.map((h) => h.value);
          return new WebTransportTransport(`${offered.url}${sep}ticket=${ticket}`, hashes, deps.createWebTransport);
        }
      : null;
  const loc = deps.location ?? globalThis.location;
  const transport = await raceTransports({
    mode: opts.transport,
    startWebTransport,
    webTransportUnavailable: offered
      ? "WebTransport is not supported by this browser"
      : "The server does not offer WebTransport",
    startWebSocket: () => new WebSocketTransport(websocketUrl(netInfo.websocketPath, ticket, loc), deps.createWebSocket),
    fallbackMs: deps.fallbackMs,
  });

  const conn = new GameConnection(transport, codec, deps.env);
  conn.send(
    create(ClientMessageSchema, {
      msg: { case: "hello", value: create(HelloSchema, { protocolVersion: PROTOCOL_VERSION }) },
    }),
  );
  conn.start();
  return conn;
}
